import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Grid,
  GridItem,
  Icon,
  Badge,
  Flex,
  Divider,
  Link,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import OptimizedImage from '../components/common/OptimizedImage';
import {
  FaTrophy,
  FaStar,
  FaPhone,
  FaEnvelope,
  FaChevronRight,
  FaCheckCircle,
  FaBuilding,
  FaHandshake,
} from 'react-icons/fa';
import { PageHero, SectionHeading, IconMark, InkBand, LightSheet } from '../components/premium';
import { Reveal, RevealGroup, RevealItem } from '../components/motion';
import { clearGlass } from '../components/glass';

// One quiet card surface for the page: white, hairline edge, soft ink
// shadow, and a slow settle upward on hover.
const card = {
  bg: 'white',
  borderRadius: '24px',
  boxShadow: 'card',
  border: '1px solid',
  borderColor: 'rgba(14,47,58,0.06)',
  transition: 'transform 0.5s cubic-bezier(0.16,1,0.3,1)',
  _hover: { transform: 'translateY(-3px)' },
};

// Small tracked capitals, used for labels and badges instead of colored pills.
const quietLabel = {
  fontSize: 'xs',
  fontWeight: '600',
  textTransform: 'uppercase',
  letterSpacing: '0.14em',
  px: 3,
  py: 1.5,
  borderRadius: 'pill',
  bg: 'mist.100',
  color: 'ink.700',
};

const Sponsors = () => {
  // Existing sponsor partners
  const existingSponsors = [
    {
      name: 'HST Pathways',
      logo: '/sponsors/HST_logo.png',
      website: 'https://www.hstpathways.com',
    },
    {
      name: 'SYNERGEN Health',
      logo: '/sponsors/Synergen_logo.png',
      website: 'https://www.synergenhealth.com',
    },
    {
      name: 'RFX',
    },
  ];

  return (
    <Box>
      {/* Photographic hero, shared with every inner page. The "Limited
          Opportunity" badge becomes the glass eyebrow and the three facts
          ride below as glass chips, all with their wording unchanged. */}
      <PageHero
        eyebrow="Limited Opportunity"
        title="Become a Founding Sponsor"
        subtitle="Partner with the region's largest and most innovative ambulatory surgery center. Be part of something extraordinary as we redefine surgical excellence."
        image="/facility/exterior.jpg"
        imagePosition="center"
      >
        <HStack spacing={3} pt={8} flexWrap="wrap" justify="center">
          {[
            { icon: FaBuilding, label: '30 Operating Rooms' },
            { icon: FaHandshake, label: '19 Specialties' },
            { icon: FaStar, label: 'Innovation Leader' },
          ].map((fact) => (
            <Flex
              key={fact.label}
              align="center"
              gap={2.5}
              px={4}
              py={2.5}
              borderRadius="pill"
              sx={clearGlass}
            >
              <Icon as={fact.icon} boxSize={4} color="starOfLife.300" />
              <Text fontSize="md" fontWeight="600">
                {fact.label}
              </Text>
            </Flex>
          ))}
        </HStack>
      </PageHero>

      {/* Innovation Center — the lead statement for this page. A deep ink
          panel rather than a cyan gradient; it is not a sheet because it
          sits directly under the hero. */}
      <Container maxW="container.xl" py={{ base: 12, md: 20 }}>
        <Reveal>
          <Box
            bg="brand.900"
            borderRadius={{ base: '24px', md: '32px' }}
            p={{ base: 8, md: 16 }}
            boxShadow="card"
          >
            <VStack spacing={6} textAlign="center">
              <HStack spacing={2.5} justify="center">
                <Box w="6px" h="6px" borderRadius="full" bg="starOfLife.300" />
                <Text
                  fontSize="sm"
                  fontWeight="600"
                  textTransform="uppercase"
                  letterSpacing="0.18em"
                  color="whiteAlpha.800"
                >
                  The Innovation Center
                </Text>
              </HStack>
              <Text
                fontSize={{ base: 'xl', md: '2xl' }}
                color="white"
                lineHeight="1.6"
                fontWeight="400"
                maxW="4xl"
              >
                Our dedicated Innovation Center is a platform for forward-thinking organizations aligned with our vision to drive clinical
                and technological innovation in all stages of patient care. Partners will have the opportunity to showcase and integrate
                leading-edge solutions in surgical tools, implants, processes, and systems that elevate surgeon performance,
                operational efficiency, and patient experience.
              </Text>
            </VStack>
          </Box>
        </Reveal>
      </Container>

      {/* Naming opportunity — a supporting note, deliberately quieter than the
          Innovation Center statement above it. */}
      <Container maxW="container.xl" pb={{ base: 12, md: 16 }}>
        <Reveal>
          <Box {...card} p={{ base: 6, md: 8 }}>
            <Flex
              direction={{ base: 'column', md: 'row' }}
              align={{ base: 'flex-start', md: 'center' }}
              gap={{ base: 5, md: 7 }}
            >
              <IconMark icon={FaTrophy} />

              <Box flex="1">
                <Heading size="md" color="ink.900" fontWeight="600" mb={2}>
                  Name the Teaching O.R.
                </Heading>
                <Text color="ink.600" fontSize="md" lineHeight="1.62">
                  Like naming rights on a stadium, one exclusive partner will have their name
                  permanently displayed on our state-of-the-art teaching operating room — a
                  centerpiece of medical education for decades to come.
                </Text>
              </Box>

              <VStack
                spacing={2}
                flexShrink={0}
                align={{ base: 'flex-start', md: 'flex-end' }}
              >
                <Badge {...quietLabel}>
                  Only 1 Spot Available
                </Badge>
                <Text
                  fontSize="xs"
                  color="ink.500"
                  fontStyle="italic"
                  textAlign={{ base: 'left', md: 'right' }}
                >
                  Before our Chicago showcase
                </Text>
              </VStack>
            </Flex>
          </Box>
        </Reveal>
      </Container>

      {/* Founding Partner Wall Section */}
      <Container maxW="container.xl" py={{ base: 16, md: 20 }}>
        <SectionHeading title="Founding Partner Wall" />
        {/* Subtitle removed per design update */}

        <RevealGroup
          display="grid"
          gridTemplateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }}
          gap={6}
          w="100%"
        >
          {/* Existing Sponsor Partners */}
          {existingSponsors.map((sponsor, index) => (
            <RevealItem key={`sponsor-${index}`}>
              <Box
                {...card}
                p={8}
                textAlign="center"
                minH="200px"
                h="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                gap={4}
                as={sponsor.website ? 'a' : 'div'}
                href={sponsor.website || undefined}
                target={sponsor.website ? '_blank' : undefined}
                rel={sponsor.website ? 'noopener noreferrer' : undefined}
                cursor={sponsor.website ? 'pointer' : 'default'}
              >
                {sponsor.logo ? (
                  <OptimizedImage
                    src={sponsor.logo}
                    alt={sponsor.name}
                    w="200px"
                    h="100px"
                    maxW="100%"
                    objectFit="contain"
                  />
                ) : (
                  <Text
                    fontSize="2xl"
                    fontWeight="600"
                    color="ink.900"
                    letterSpacing="0.12em"
                    textTransform="uppercase"
                  >
                    {sponsor.name}
                  </Text>
                )}
                <Badge {...quietLabel}>
                  Founding Partner
                </Badge>
                {sponsor.description && (
                  <Text fontSize="xs" color="ink.500" fontStyle="italic">
                    {sponsor.description}
                  </Text>
                )}
              </Box>
            </RevealItem>
          ))}

          {/* Available Spots */}
          {[...Array(Math.max(0, 3 - existingSponsors.length))].map((_, index) => (
            <RevealItem key={`available-${index}`}>
              <Box
                bg="white"
                borderRadius="24px"
                p={8}
                textAlign="center"
                border="1px dashed"
                borderColor="rgba(14,47,58,0.18)"
                transition="transform 0.5s cubic-bezier(0.16,1,0.3,1)"
                _hover={{ transform: 'translateY(-3px)' }}
                minH="200px"
                h="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <VStack spacing={3}>
                  <IconMark icon={FaStar} />
                  <Text
                    fontSize="lg"
                    fontWeight="600"
                    color="ink.500"
                    textTransform="uppercase"
                    letterSpacing="0.14em"
                  >
                    Your Logo Here
                  </Text>
                  <Badge {...quietLabel}>
                    Available
                  </Badge>
                </VStack>
              </Box>
            </RevealItem>
          ))}
        </RevealGroup>
      </Container>

      {/* Why Partner Section — a quiet mist sheet in place of the pale cyan
          gradient. */}
      <LightSheet tone="mist" py={{ base: 16, md: 20 }}>
        <Container maxW="container.xl">
          <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={12} alignItems="center">
            <GridItem>
              <VStack align="start" spacing={6}>
                <SectionHeading align="left" mb={0} title="Why Partner With Us?" />

                <RevealGroup
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                  gap={4}
                >
                  <RevealItem>
                    <HStack align="start" spacing={4}>
                      <Box
                        w="6px"
                        h="6px"
                        bg="brand.500"
                        borderRadius="full"
                        mt={2.5}
                        flexShrink={0}
                      />
                      <Text fontSize="lg" color="ink.700" lineHeight="tall">
                        <strong>Regional Leadership:</strong> The largest ASC facility in the area with 30 state-of-the-art operating rooms
                      </Text>
                    </HStack>
                  </RevealItem>

                  <RevealItem>
                    <HStack align="start" spacing={4}>
                      <Box
                        w="6px"
                        h="6px"
                        bg="starOfLife.500"
                        borderRadius="full"
                        mt={2.5}
                        flexShrink={0}
                      />
                      <Text fontSize="lg" color="ink.700" lineHeight="tall">
                        <strong>Innovation Pioneer:</strong> Leading the way with outdoor ERAS programs and cutting-edge recovery protocols
                      </Text>
                    </HStack>
                  </RevealItem>

                  <RevealItem>
                    <HStack align="start" spacing={4}>
                      <Box
                        w="6px"
                        h="6px"
                        bg="brand.500"
                        borderRadius="full"
                        mt={2.5}
                        flexShrink={0}
                      />
                      <Text fontSize="lg" color="ink.700" lineHeight="tall">
                        <strong>Physician Excellence:</strong> Board-certified specialists across 19 surgical specialties
                      </Text>
                    </HStack>
                  </RevealItem>

                  <RevealItem>
                    <HStack align="start" spacing={4}>
                      <Box
                        w="6px"
                        h="6px"
                        bg="starOfLife.500"
                        borderRadius="full"
                        mt={2.5}
                        flexShrink={0}
                      />
                      <Text fontSize="lg" color="ink.700" lineHeight="tall">
                        <strong>Educational Hub:</strong> Teaching facility with conference center and inactive OR for training
                      </Text>
                    </HStack>
                  </RevealItem>

                  <RevealItem>
                    <HStack align="start" spacing={4}>
                      <Box
                        w="6px"
                        h="6px"
                        bg="brand.500"
                        borderRadius="full"
                        mt={2.5}
                        flexShrink={0}
                      />
                      <Text fontSize="lg" color="ink.700" lineHeight="tall">
                        <strong>Patient-Centric:</strong> Concierge-style care with data-driven operations and measurable outcomes
                      </Text>
                    </HStack>
                  </RevealItem>
                </RevealGroup>
              </VStack>
            </GridItem>

            <GridItem>
              <Reveal>
                <Box {...card} p={{ base: 8, md: 10 }}>
                  <VStack spacing={6} align="stretch">
                    <Heading size="lg" color="ink.900" fontWeight="600" textAlign="center">
                      Exclusive Benefits
                    </Heading>

                    <Divider borderColor="blackAlpha.100" />

                    <RevealGroup
                      display="flex"
                      flexDirection="column"
                      alignItems="flex-start"
                      gap={4}
                    >
                      <RevealItem>
                        <HStack spacing={3}>
                          <Icon as={FaCheckCircle} color="brand.600" boxSize={5} />
                          <Text fontWeight="500" color="ink.800">Brand visibility across 30 ORs</Text>
                        </HStack>
                      </RevealItem>
                      <RevealItem>
                        <HStack spacing={3}>
                          <Icon as={FaCheckCircle} color="brand.600" boxSize={5} />
                          <Text fontWeight="500" color="ink.800">Access to physician network</Text>
                        </HStack>
                      </RevealItem>
                      <RevealItem>
                        <HStack spacing={3}>
                          <Icon as={FaCheckCircle} color="brand.600" boxSize={5} />
                          <Text fontWeight="500" color="ink.800">Marketing co-opportunities</Text>
                        </HStack>
                      </RevealItem>
                      <RevealItem>
                        <HStack spacing={3}>
                          <Icon as={FaCheckCircle} color="brand.600" boxSize={5} />
                          <Text fontWeight="500" color="ink.800">Educational event participation</Text>
                        </HStack>
                      </RevealItem>
                      <RevealItem>
                        <HStack spacing={3}>
                          <Icon as={FaCheckCircle} color="brand.600" boxSize={5} />
                          <Text fontWeight="500" color="ink.800">Community impact recognition</Text>
                        </HStack>
                      </RevealItem>
                    </RevealGroup>

                    <Divider borderColor="blackAlpha.100" />

                    <Badge {...quietLabel} fontSize="sm" px={4} py={2} textAlign="center">
                      ⏰ Limited Time - Act Now
                    </Badge>
                  </VStack>
                </Box>
              </Reveal>
            </GridItem>
          </Grid>
        </Container>
      </LightSheet>

      {/* CTA Section — deep ink band in place of the blue-to-cyan gradient
          card. */}
      <InkBand image="/facility/exterior.jpg">
        <Container maxW="container.xl">
          <Reveal>
            <VStack spacing={8} textAlign="center" maxW="4xl" mx="auto">
              <Heading size="2xl" color="white" fontWeight="600" letterSpacing="-0.022em" lineHeight="1.15">
                Secure Your Founding Partnership Today
              </Heading>

              <Text fontSize="xl" color="whiteAlpha.900" maxW="3xl" lineHeight="tall">
                Don't miss this exclusive opportunity to be recognized as a founding partner
                of the region's premier ambulatory surgery center. Contact us now to discuss
                partnership opportunities.
              </Text>

              <Divider borderColor="whiteAlpha.300" />

              <VStack spacing={4}>
                <Heading size="md" color="white" fontWeight="600">
                  Get Started Now
                </Heading>

                <HStack spacing={6} flexWrap="wrap" justify="center">
                  <HStack>
                    <Icon as={FaEnvelope} boxSize={5} color="starOfLife.300" />
                    <Link
                      href="mailto:ibrahim@lucienasc.com"
                      fontSize="lg"
                      fontWeight="600"
                      color="white"
                      _hover={{ color: 'white', textDecoration: 'underline' }}
                    >
                      ibrahim@lucienasc.com
                    </Link>
                  </HStack>

                  <HStack>
                    <Icon as={FaPhone} boxSize={5} color="starOfLife.300" />
                    <Link
                      href="tel:+13213213935"
                      fontSize="lg"
                      fontWeight="600"
                      color="white"
                      _hover={{ color: 'white', textDecoration: 'underline' }}
                    >
                      (321) 321-3935
                    </Link>
                  </HStack>
                </HStack>
              </VStack>

              <HStack spacing={4} pt={4} flexWrap="wrap" justify="center">
                <Button
                  as="a"
                  href="mailto:ibrahim@lucienasc.com?subject=Founding%20Sponsor%20Inquiry"
                  size="lg"
                  px={10}
                  py={7}
                  variant="brandOnDark"
                  fontSize="lg"
                  rightIcon={<FaChevronRight />}
                >
                  Partner With Us
                </Button>

                <Button
                  as={RouterLink}
                  to="/contact"
                  size="lg"
                  px={10}
                  py={7}
                  color="white"
                  fontSize="lg"
                  fontWeight="600"
                  sx={clearGlass}
                  transition="transform 0.35s cubic-bezier(0.16,1,0.3,1), background-color 0.2s"
                  _hover={{ bg: 'whiteAlpha.200', transform: 'translateY(-2px)' }}
                  _active={{ bg: 'whiteAlpha.300' }}
                  rightIcon={<FaChevronRight />}
                >
                  Contact Form
                </Button>
              </HStack>

              <Text fontSize="sm" color="whiteAlpha.800" fontStyle="italic">
                Limited founding positions available • Deadline approaching for Chicago showcase
              </Text>
            </VStack>
          </Reveal>
        </Container>
      </InkBand>
    </Box>
  );
};

export default Sponsors;
